<script setup lang="ts">
import { ref, watch } from 'vue'
import type { PartConstraint } from '../api/competitions'
import { searchParts, type PartResult } from '../api/parts'

const props = defineProps<{
	modelValue: PartConstraint[]
}>()

const emit = defineEmits<{
	'update:modelValue': [value: PartConstraint[]]
}>()

const query = ref('')
const results = ref<PartResult[]>([])
const partTitles = ref<Record<string, string>>({})
let debounceTimer: ReturnType<typeof setTimeout> | null = null

// Build title lookup for currently selected parts on init
watch(() => props.modelValue, async (constraints) => {
	const idsToResolve = constraints
		.map(c => c.part_id)
		.filter(id => !partTitles.value[id])

	if (!idsToResolve.length) return

	// Search for each unknown ID to resolve its title
	for (const id of idsToResolve) {
		const found = await searchParts(id)
		const match = found.find(p => p.name === id)
		if (match) partTitles.value[id] = match.title
	}
}, { immediate: true })

function onQueryInput() {
	if (debounceTimer) clearTimeout(debounceTimer)
	const q = query.value.trim()
	if (q.length < 2) {
		results.value = []
		return
	}
	debounceTimer = setTimeout(async () => {
		results.value = await searchParts(q)
	}, 200)
}

function addPart(part: PartResult) {
	if (props.modelValue.some(c => c.part_id === part.name)) {
		query.value = ''
		results.value = []
		return
	}
	partTitles.value[part.name] = part.title
	emit('update:modelValue', [
		...props.modelValue,
		{ part_id: part.name, type: 'required', count: 1 },
	])
	query.value = ''
	results.value = []
}

function removePart(partId: string) {
	emit('update:modelValue', props.modelValue.filter(c => c.part_id !== partId))
}

function updateConstraint(partId: string, field: 'type' | 'count', value: string | number) {
	emit('update:modelValue', props.modelValue.map(c => {
		if (c.part_id !== partId) return c
		if (field === 'type') {
			const type = value as PartConstraint['type']
			const count = (type === 'at_least' || type === 'at_most') ? (c.count || 1) : 1
			return { ...c, type, count }
		}
		return { ...c, count: Number(value) }
	}))
}

function getTitle(partId: string) {
	return partTitles.value[partId] ?? partId
}

function needsCount(type: PartConstraint['type']) {
	return type === 'at_least' || type === 'at_most'
}
</script>

<template>
	<div class="c-part-constraint-editor">
		<div class="c-part-constraint-editor__search">
			<input
				v-model="query"
				type="text"
				placeholder="Search parts by name or ID…"
				class="c-part-constraint-editor__input"
				autocomplete="off"
				@input="onQueryInput"
				@blur="setTimeout(() => { results = [] }, 150)"
			/>
			<ul v-if="results.length" class="c-part-constraint-editor__dropdown">
				<li
					v-for="part in results"
					:key="part.name"
					class="c-part-constraint-editor__result"
					@mousedown.prevent="addPart(part)"
				>
					{{ part.title }} <span class="c-part-constraint-editor__id">({{ part.name }})</span>
				</li>
			</ul>
		</div>

		<p v-if="!modelValue.length" class="c-part-constraint-editor__empty">
			No part constraints added.
		</p>

		<ul v-else class="c-part-constraint-editor__list">
			<li
				v-for="constraint in modelValue"
				:key="constraint.part_id"
				class="c-part-constraint-editor__row"
			>
				<button
					type="button"
					class="c-part-constraint-editor__remove"
					title="Remove"
					@click="removePart(constraint.part_id)"
				>×</button>

				<span class="c-part-constraint-editor__part-name">
					{{ getTitle(constraint.part_id) }}
					<span class="c-part-constraint-editor__id">({{ constraint.part_id }})</span>
				</span>

				<select
					:value="constraint.type"
					class="c-part-constraint-editor__type"
					@change="updateConstraint(constraint.part_id, 'type', ($event.target as HTMLSelectElement).value)"
				>
					<option value="required">Required</option>
					<option value="not_allowed">Not Allowed</option>
					<option value="at_least">At Least</option>
					<option value="at_most">At Most</option>
				</select>

				<input
					v-if="needsCount(constraint.type)"
					type="number"
					:value="constraint.count"
					min="1"
					max="999"
					class="c-part-constraint-editor__count"
					@change="updateConstraint(constraint.part_id, 'count', ($event.target as HTMLInputElement).valueAsNumber)"
				/>
			</li>
		</ul>
	</div>
</template>

<style scoped>
.c-part-constraint-editor {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.c-part-constraint-editor__search {
	position: relative;
}

.c-part-constraint-editor__input {
	all: inherit;
	display: block;
	width: 100%;
	padding: 0.5rem;
	margin: unset;
	line-height: 1;
	background: var(--color-form-field-bg);
	border: 2px solid var(--color-form-field-border);
	box-sizing: border-box;
}

.c-part-constraint-editor__input:focus {
	border-color: var(--color-primary);
	outline: none;
}

.c-part-constraint-editor__dropdown {
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	z-index: 10;
	margin: 0;
	padding: 0;
	list-style: none;
	border: 2px solid var(--color-form-field-border);
	border-top: none;
	background: var(--color-form-field-bg);
	max-height: 14rem;
	overflow-y: auto;
}

.c-part-constraint-editor__result {
	padding: 0.4rem 0.6rem;
	cursor: pointer;
}

.c-part-constraint-editor__result:hover {
	background: var(--color-bg-secondary);
}

.c-part-constraint-editor__id {
	opacity: 0.6;
	font-size: 0.85em;
}

.c-part-constraint-editor__empty {
	opacity: 0.6;
	font-style: italic;
	margin: 0;
}

.c-part-constraint-editor__list {
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
}

.c-part-constraint-editor__row {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
}

.c-part-constraint-editor__remove {
	flex-shrink: 0;
	padding: 0 0.4rem;
	font-size: 1rem;
	line-height: 1.6;
	all: inherit;
	display: inline-block;
	border: 2px solid var(--color-form-field-border);
	background: var(--color-form-field-bg);
	cursor: pointer;
}

.c-part-constraint-editor__remove:hover {
	border-color: var(--color-primary);
}

.c-part-constraint-editor__part-name {
	flex: 1;
	min-width: 0;
}

.c-part-constraint-editor__type,
.c-part-constraint-editor__count {
	all: inherit;
	padding: 0.5rem;
	margin: unset;
	line-height: 1;
	background: var(--color-form-field-bg);
	border: 2px solid var(--color-form-field-border);
}

.c-part-constraint-editor__type:focus,
.c-part-constraint-editor__count:focus {
	border-color: var(--color-primary);
	outline: none;
}

.c-part-constraint-editor__count {
	width: 5rem;
}
</style>
